import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../utils/auth';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import API_BASE_URL from '../config';

export default function Vehicles() {
  const { isEditDisabled } = useAuth();
  // Helper to format date to Sri Lanka time (GMT+5:30)
  const formatSLTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // Sri Lanka time offset in minutes: 5*60 + 30 = 330
    const offsetDate = new Date(date.getTime() + (330 - date.getTimezoneOffset()) * 60000);
    return offsetDate.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' (GMT+5:30)';
  };
  const [editOpen, setEditOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [editForm, setEditForm] = useState({ license_plate: '', device_id: '', status: 1 });
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ license_plate: '', device_id: '', status: 1 });
  const [creating, setCreating] = useState(false);

  const handleEditClick = (vehicle) => {
    if (isEditDisabled) return;
    setEditVehicle(vehicle);
    setEditForm({
      license_plate: vehicle.license_plate || '',
      device_id: vehicle.device_id || '',
      status: typeof vehicle.status === 'number' ? vehicle.status : 1
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });

  };

  const handleCreateChange = (e) => {
    setCreateForm({ ...createForm, [e.target.name]: e.target.value });
  };

  const handleStatusChange = (e) => {
    setEditForm({ ...editForm, status: e.target.checked ? 1 : 0 });
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/vehicles/${editVehicle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('Failed to update vehicle');
      // Update local state
      setVehicles((prev) => prev.map(v => v.id === editVehicle.id ? { ...v, ...editForm, updated_at: new Date().toISOString() } : v));
      setEditOpen(false);
      setEditVehicle(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditVehicle(null);
  };
  const handleCreateOpen = () => {
    if (isEditDisabled) return;
    setCreateForm({ license_plate: '', device_id: '', status: 1 });
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
  };

  const handleCreateSave = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/vehicles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to create vehicle');
      }
      const newVehicle = await res.json();
      setVehicles((prev) => [newVehicle, ...prev]);
      setCreateOpen(false);
    } catch (e) {
      alert(e.message || 'Error creating vehicle');
    } finally {
      setCreating(false);
    }
  };
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  // update logs modal state
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [logsForVehicle, setLogsForVehicle] = useState([]);
  const [logsVehicleInfo, setLogsVehicleInfo] = useState(null);

  const filteredVehicles = useMemo(() => {
    if (!query) return vehicles;
    const q = String(query).toLowerCase();
    return vehicles.filter(v => (
      String(v.license_plate || '').toLowerCase().includes(q) ||
      String(v.device_id || '').toLowerCase().includes(q) ||
      String(v.id || '').toLowerCase().includes(q)
    ));
  }, [vehicles, query]);

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/vehicles`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch vehicles');
        const data = await res.json();
        setVehicles(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  // fetch update logs for a vehicle and open modal
  const openLogsForVehicle = async (vehicle) => {
    setLogsVehicleInfo(vehicle);
    setLogsOpen(true);
    setLogsLoading(true);
    setLogsError('');
    setLogsForVehicle([]);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/vehicles/${vehicle.id}/update-logs`, {
        headers: {
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch update logs');
      const data = await res.json();
      setLogsForVehicle(Array.isArray(data) ? data : (data.logs || data));
    } catch (e) {
      setLogsError(e.message || 'Unknown error');
    } finally {
      setLogsLoading(false);
    }
  };

  const closeLogs = () => {
    setLogsOpen(false);
    setLogsForVehicle([]);
    setLogsVehicleInfo(null);
    setLogsError('');
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>Vehicles</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField placeholder="Search vehicles" value={query} onChange={(e) => setQuery(e.target.value)} size="small" sx={{ minWidth: 200 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleCreateOpen} disabled={isEditDisabled}>Create New Vehicle</Button>
        </Box>
      </Box>
      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
    {!loading && vehicles.length > 0 && (
  <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>License Plate</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>View Update Log</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVehicles.length > 0 ? filteredVehicles.map((vehicle,vehicleIndex) => (
                <TableRow key={vehicle.id}>
                  <TableCell>{vehicleIndex+1}</TableCell>
                  <TableCell>{vehicle.license_plate}</TableCell>
                  <TableCell>{vehicle.device_id}</TableCell>
                  <TableCell>{formatSLTime(vehicle.updated_at)}</TableCell>
                   <TableCell>
                     {vehicle.status === 1 ? (
                       <span style={{ color: 'white', backgroundColor: 'green', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Active</span>
                     ) : (
                       <span style={{ color: 'white', backgroundColor: 'red', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Inactive</span>
                     )}
                   </TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => handleEditClick(vehicle)} disabled={isEditDisabled}>Edit</Button>
                  </TableCell>
                  <TableCell>
                    <Button variant="contained" size="small" onClick={() => openLogsForVehicle(vehicle)}>View Update Log</Button>
                  </TableCell>
                  {editOpen && editVehicle && editVehicle.id === vehicle.id && (
                    <Modal open={editOpen} onClose={handleEditClose}>
                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, minWidth: 320 }}>
                        <Typography variant="h6" gutterBottom>Edit Vehicle</Typography>
                        <TextField label="License Plate" name="license_plate" value={editForm.license_plate} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
                        <TextField label="Device ID" name="device_id" value={editForm.device_id} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editForm.status === 1}
                              onChange={handleStatusChange}
                              color="success"
                            />
                          }
                          label={editForm.status === 1 ? "Active" : "Inactive"}
                          sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                          <Button onClick={handleEditClose} disabled={saving}>Cancel</Button>
                          <Button variant="contained" onClick={handleEditSave} disabled={saving || isEditDisabled}>Save</Button>
                        </Box>
                      </Box>
                    </Modal>
                  )}
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No vehicles match your search.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

        {/* Create Modal */}
        <Modal open={createOpen} onClose={handleCreateClose}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, minWidth: 320 }}>
            <Typography variant="h6" gutterBottom>Create Vehicle</Typography>
            <TextField label="License Plate" name="license_plate" value={createForm.license_plate} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
            <TextField label="Device ID" name="device_id" value={createForm.device_id} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.status === 1}
                  onChange={(e) => setCreateForm({ ...createForm, status: e.target.checked ? 1 : 0 })}
                  color="success"
                />
              }
              label={createForm.status === 1 ? "Active" : "Inactive"}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleCreateClose} disabled={creating}>Cancel</Button>
              <Button variant="contained" onClick={handleCreateSave} disabled={creating || isEditDisabled}>Create</Button>
            </Box>
          </Box>
        </Modal>

        {/* Logs Modal */}
        <Modal open={logsOpen} onClose={closeLogs}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, minWidth: 320 }}>
            <Typography variant="h6" gutterBottom>Update Logs for {logsVehicleInfo?.license_plate}</Typography>
            {logsLoading && <Typography>Loading logs...</Typography>}
            {logsError && <Typography color="error">{logsError}</Typography>}
            {!logsLoading && logsForVehicle.length === 0 && (
              <Typography>No update logs found for this vehicle.</Typography>
            )}
            {logsForVehicle.length != 0 &&(<TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Old Value</TableCell>
                    <TableCell>New Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsForVehicle.flatMap((entry, entryIndex) => {
                    const ts = entry.updated_at || entry.created_at || '';
                    const changes = entry.changes || {};
                    const keys = Object.keys(changes);
                    if (keys.length === 0) {
                      return [(
                        <TableRow key={`entry-${entryIndex}`}>
                          <TableCell>{(ts)}</TableCell>
                          <TableCell colSpan={3}><pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(entry, null, 2)}</pre></TableCell>
                        </TableRow>
                      )];
                    }
                    return keys.map((field) => {
                      const val = changes[field] || {};
                      return (
                        <TableRow key={`entry-${entryIndex}-${field}`}>
                          <TableCell>{(ts)}</TableCell>
                          <TableCell>{field}</TableCell>
                          <TableCell>{String(val.before ?? '')}</TableCell>
                          <TableCell>{String(val.after ?? '')}</TableCell>
                        </TableRow>
                      );
                    });
                  })}
                </TableBody>
              </Table>
            </TableContainer>)}
          </Box>
        </Modal>
    </div>
  );
}
